import { useCallback, useEffect, useMemo, useRef } from 'react';

import * as Sentry from '@sentry/nextjs';
import { isAxiosError } from 'axios';

import { RawRESTClient } from 'src/apis/service';
import { SessionAccessService, SessionAccessTrigger } from 'src/apis/service/SessionAccessService';
import useLogin from 'src/hooks/useLogin';
import useVisibilityChange from 'src/hooks/useVisibilityChange';
import { getLoginParam } from 'src/utils/auth';
import { isPwaMode } from 'src/utils/device/info';
import { safeWindow } from 'src/utils/static-generation';

const THROTTLE_MS = 10_000;

const useSessionAccess = () => {
  const { isLogin } = useLogin();
  const lastCalledAtRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);

  const loginParam = useMemo(() => (safeWindow ? getLoginParam() : undefined), []);

  const sendAccess = useCallback(
    (trigger: SessionAccessTrigger) => {
      if (!isLogin || !loginParam) return;

      const now = Date.now();
      if (now - lastCalledAtRef.current < THROTTLE_MS) {
        return;
      }

      lastCalledAtRef.current = now;

      const service = new SessionAccessService(new RawRESTClient());
      service
        .reportAccess({
          deviceId: loginParam.deviceId,
          clientProperties: loginParam.clientProperties,
          trigger,
          webContext: {
            displayMode: isPwaMode() ? 'standalone' : 'browser',
          },
        })
        .catch((error) => {
          // 실패 시 대응하지 않아도 무방하나 혹시 모를 400번대 에러만 로깅
          if (
            isAxiosError(error) &&
            error.response?.status &&
            error.response.status >= 400 &&
            error.response.status < 500
          ) {
            Sentry.captureException(error);
          }
        });
    },
    [isLogin, loginParam]
  );

  useEffect(() => {
    if (!isLogin) return;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      const navEntry = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      sendAccess(navEntry?.type === 'reload' ? 'reload' : 'load');
    }
  }, [isLogin, sendAccess]);

  useEffect(() => {
    if (!isLogin) {
      // 로그아웃 시 초기화
      lastCalledAtRef.current = 0;
      isInitialLoadRef.current = true;
    }
  }, [isLogin]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      sendAccess('resume');
    }
  }, [sendAccess]);

  useVisibilityChange(handleVisibilityChange);
};

export default useSessionAccess;
